import { AlertCircle, AlertOctagon, GitMerge } from "lucide-react";
import React, { useMemo } from "react";
import { isReadyToMerge, needsMyReview } from "../../quickFilters";
import { Tooltip } from "../Tooltip";

// Shared by the grouped views (By Repository, By Jira).

// Sort: drafts last, then by the selected date field (most recent first).
export const createSorter = (sortOption) => (a, b) => {
	if (a.draft && !b.draft) return 1;
	if (!a.draft && b.draft) return -1;
	const field = sortOption === "createdAt" ? "createdAt" : "updatedAt";
	return new Date(b[field]) - new Date(a[field]);
};

// Open MRs from both lists, de-duplicated by id (an MR can be both authored
// by and assigned to the viewer).
export const collectOpenMRs = (...lists) => {
	const seen = new Set();
	const openMRs = [];
	for (const mr of lists.flat()) {
		if (mr.state !== "opened" || seen.has(mr.id)) continue;
		seen.add(mr.id);
		openMRs.push(mr);
	}
	return openMRs;
};

const isCiFailing = (mr) =>
	mr.headPipeline?.status?.toLowerCase() === "failed" ||
	(mr.headPipeline?.failedJobs ?? 0) > 0;

const Chip = ({ icon: Icon, count, label, className }) => {
	if (!count) return null;
	return (
		<Tooltip content={`${count} ${label}`}>
			<span
				className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full border ${className}`}
			>
				<Icon className="w-3.5 h-3.5" />
				{count}
			</span>
		</Tooltip>
	);
};

// Needs-review / ready / failing-CI counts for a lane; renders nothing when
// all three are zero.
export const LaneStats = ({ mrs, className = "" }) => {
	const stats = useMemo(() => {
		let needsReview = 0;
		let ready = 0;
		let failing = 0;
		for (const mr of mrs) {
			if (needsMyReview(mr)) needsReview++;
			if (isReadyToMerge(mr)) ready++;
			if (isCiFailing(mr)) failing++;
		}
		return { needsReview, ready, failing };
	}, [mrs]);

	if (!stats.needsReview && !stats.ready && !stats.failing) return null;

	return (
		<div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
			<Chip
				icon={AlertOctagon}
				count={stats.needsReview}
				label="need your review"
				className="border-orange-500/40 bg-orange-500/10 text-[var(--status-attention-text)]"
			/>
			<Chip
				icon={GitMerge}
				count={stats.ready}
				label="ready to merge"
				className="border-green-500/40 bg-green-500/10 text-[var(--status-ready-text)]"
			/>
			<Chip
				icon={AlertCircle}
				count={stats.failing}
				label="with failing CI"
				className="border-red-500/40 bg-red-500/10 text-[var(--status-danger-text)]"
			/>
		</div>
	);
};
